import { z } from 'zod';

import { intervalSchema, planKeySchema } from '@/lib/plan-catalog';

/* תשובות /platform-admin/* — ספירות בלבד (ראו src/modules/platform-admin בשרת). */

const n = z.number().int().nonnegative();

export const RANGE_OPTIONS = [7, 30, 90] as const;
export type RangeDays = (typeof RANGE_OPTIONS)[number];

export const overviewSchema = z.object({
  days: n,
  businesses: z.object({ total: n, active7: n, active30: n, newInRange: n }),
  users: z.object({ total: n, dau: n, wau: n, mau: n }),
  signupsPerWeek: z.array(z.object({ week: z.string(), businesses: n })),
  byTrade: z.array(z.object({ trade: z.string(), businesses: n })),
  byLanguage: z.array(z.object({ locale: z.string(), businesses: n })),
});
export type Overview = z.infer<typeof overviewSchema>;

export const usageSchema = z.object({
  days: n,
  dailyActive: z.array(z.object({ day: z.string(), users: n, businesses: n })),
  topRoutes: z.array(z.object({ route: z.string(), views: n, users: n, businesses: n })),
  topClicks: z.array(z.object({ code: z.string(), clicks: n, users: n, businesses: n })),
  topActions: z.array(z.object({ action: z.string(), count: n, businesses: n })),
  sessionsByScreen: z.array(z.object({ screen: z.string(), sessions: n })),
  sessionsByLanguage: z.array(z.object({ lang: z.string(), sessions: n })),
  adoption: z.object({
    activeBusinesses: n,
    features: z.array(z.object({ feature: z.string(), businesses: n })),
  }),
  funnel: z.object({ signedUp: n, onboarded: n, firstWork: n, week2Eligible: n, activeWeek2: n }),
});
export type Usage = z.infer<typeof usageSchema>;

export const businessRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  subdomain: z.string(),
  trade: z.string().nullable(),
  vertical: z.string(),
  locale: z.string(),
  isActive: z.boolean(),
  /** מושעה ע"י המפעיל / מתוכנן למחיקה (migration 0040). */
  suspendedAt: z.string().nullable().optional().default(null),
  deletionScheduledFor: z.string().nullable().optional().default(null),
  createdAt: z.string(),
  lastActiveAt: z.string().nullable(),
  users: n,
  activeUsers: n,
  activeDays: n,
  pageViews: n,
  tasks: n,
  quotes: n,
  customers: n,
});
export type BusinessRow = z.infer<typeof businessRowSchema>;

export const businessesSchema = z.object({ days: n, total: n, rows: z.array(businessRowSchema) });

export const businessActivitySchema = z.object({
  days: n,
  business: z.object({
    id: z.string().uuid(),
    name: z.string(),
    subdomain: z.string(),
    trade: z.string().nullable(),
    locale: z.string(),
    isActive: z.boolean(),
    suspendedAt: z.string().nullable().optional().default(null),
    deletionScheduledFor: z.string().nullable().optional().default(null),
    createdAt: z.string(),
    setupStage: z.string().nullable(),
    users: n,
    tasks: n,
    quotes: n,
    customers: n,
    contracts: n,
    integrations: n,
  }),
  timeline: z.array(
    z.object({ day: z.string(), pageViews: n, clicks: n, actions: n, activeUsers: n }),
  ),
  topActions: z.array(z.object({ action: z.string(), count: n })),
  topRoutes: z.array(z.object({ route: z.string(), views: n })),
  topClicks: z.array(z.object({ code: z.string(), clicks: n })),
});
export type BusinessActivity = z.infer<typeof businessActivitySchema>;

/** סדר קבוע לאימוץ התכונות — גם תכונה שאף עסק לא השתמש בה מוצגת (0%). */
export const FEATURE_KEYS = [
  'tasks',
  'customers',
  'quotes',
  'quotesSent',
  'schedule',
  'contracts',
  'purchaseOrders',
  'supplierInvoices',
  'invoices',
  'equipment',
  'whatsapp',
  'integrations',
  'automations',
  'reports',
] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];

/* ---- פעולות המפעיל, משתמשים, יומן ודוחות (migration 0040) ---- */

export const ROLES = ['OWNER', 'MANAGER', 'VIEWER', 'FIELD'] as const;
export type Role = (typeof ROLES)[number];

/** משתמש בכל העסקים — שדות מינימליים בלבד. */
export const adminUserSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  businessName: z.string(),
  subdomain: z.string(),
  businessActive: z.boolean(),
  name: z.string(),
  email: z.string(),
  role: z.enum(ROLES),
  isActive: z.boolean(),
  mustChangePassword: z.boolean(),
  isAccountCreator: z.boolean(),
  isPlatformOperator: z.boolean(),
  mfaEnabled: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;
export const adminUsersSchema = z.object({ total: n, rows: z.array(adminUserSchema) });

/** סיסמה זמנית + הודעה מוכנה — מוצגת פעם אחת. */
export const inviteSchema = z.object({
  temporaryPassword: z.string(),
  loginUrl: z.string(),
  message: z.string(),
  waUrl: z.string().nullable(),
});
export type Invite = z.infer<typeof inviteSchema>;

export const createdBusinessSchema = inviteSchema.extend({
  business: z.object({ id: z.string().uuid(), name: z.string(), subdomain: z.string() }),
  owner: z.object({ id: z.string().uuid(), name: z.string(), email: z.string() }),
});

export const businessStatusSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
  suspendedAt: z.string().nullable(),
  deletionScheduledFor: z.string().nullable(),
});

export const auditRowSchema = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  action: z.string(),
  targetType: z.string(),
  targetTenantId: z.string().nullable(),
  targetUserId: z.string().nullable(),
  targetLabel: z.string().nullable(),
  businessName: z.string().nullable(),
  actorEmail: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  ipAddress: z.string().nullable(),
});
export type AuditRow = z.infer<typeof auditRowSchema>;
export const auditSchema = z.object({
  rows: z.array(auditRowSchema),
  nextBefore: z.string().nullable(),
});

export const GRANULARITIES = ['day', 'week', 'month'] as const;
export type Granularity = (typeof GRANULARITIES)[number];

export const ACTION_KEYS = [
  'quotesCreated',
  'quotesSent',
  'quotesApproved',
  'tasksCreated',
  'tasksClosed',
  'invoicesIssued',
  'customersCreated',
] as const;
export type ActionKey = (typeof ACTION_KEYS)[number];

const actionCounts = Object.fromEntries(ACTION_KEYS.map((k) => [k, n])) as Record<
  ActionKey,
  typeof n
>;

export const reportSchema = z.object({
  from: z.string(),
  to: z.string(),
  granularity: z.enum(GRANULARITIES),
  defaultBudgetMinor: n,
  totals: z.object({
    businesses: z.object({ total: n, new: n, active: n, suspended: n, pendingDeletion: n }),
    users: z.object({ total: n, new: n, active: n }),
    logins: z.object({ count: n, uniqueUsers: n, password: n, google: n }),
    actions: z.object({ ...actionCounts, pageViews: n, clicks: n }),
    aiCostMinor: n,
  }),
  series: z.array(
    z.object({
      bucket: z.string(),
      newBusinesses: n,
      newUsers: n,
      activeUsers: n,
      activeBusinesses: n,
      logins: n,
      pageViews: n,
      clicks: n,
      aiCostMinor: n,
      ...actionCounts,
    }),
  ),
  businesses: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      subdomain: z.string(),
      status: z.enum(['active', 'suspended', 'pendingDeletion']),
      users: n,
      activeUsers: n,
      logins: n,
      aiCostMinor: n,
      ...actionCounts,
    }),
  ),
  features: z.object({
    topRoutes: z.array(z.object({ route: z.string(), views: n, users: n, businesses: n })),
    topClicks: z.array(z.object({ code: z.string(), clicks: n, users: n, businesses: n })),
  }),
  aiBudget: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      month: z.string(),
      costMinor: n,
      budgetMinor: n,
    }),
  ),
});
export type Report = z.infer<typeof reportSchema>;
export type ReportKind = 'series' | 'businesses' | 'features' | 'ai';

/* ---- מנויים (GET /platform-admin/billing). סכומים באגורות, לפני מע"מ. ---- */

export const SUBSCRIPTION_STATUSES = [
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
  'CANCELED',
  'EXPIRED',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const businessSubscriptionSchema = z.object({
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  plan: planKeySchema,
  interval: intervalSchema,
  status: z.enum(SUBSCRIPTION_STATUSES),
  readOnly: z.boolean(),
  trialEndsAt: z.string().nullable(),
  trialDaysLeft: z.number().int().nullable(),
  currentPeriodEnd: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  pastDueSince: z.string().nullable(),
  extraSeats: n,
  pendingPlan: planKeySchema.nullable(),
  coupon: z.object({ percent: n, monthsLeft: n }).nullable(),
  complimentary: z.boolean(),
  provider: z.string().nullable(),
  /** משתמשים פעילים שנספרים במגבלה (כל משתמש = 1). */
  seatsUsed: z.number().nonnegative(),
  seatLimit: n,
  paidInvoices: n,
  mrrMinor: n,
});
export type BusinessSubscription = z.infer<typeof businessSubscriptionSchema>;

const planCounts = z.object({ paying: n, trialing: n, complimentary: n });

export const billingSummarySchema = z.object({
  mrrMinor: n,
  arrMinor: n,
  counts: z.object({
    active: n,
    trialing: n,
    pastDue: n,
    canceled: n,
    expired: n,
    complimentary: n,
  }),
  trialConversion: z.object({ ended: n, converted: n, rate: z.number().nullable() }),
  plans: z.object({
    STARTER: planCounts,
    TEAM: planCounts,
    GROWTH: planCounts,
    BUSINESS: planCounts,
  }),
  rows: z.array(businessSubscriptionSchema),
});
export type BillingSummary = z.infer<typeof billingSummarySchema>;

export const businessBillingSchema = z.object({
  subscription: businessSubscriptionSchema.nullable(),
});
